/**
 * OTD Performance Guide — documentation for On-Time Delivery settings.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import {
  BookOpen,
  Calculator,
  Clock,
  Info,
  Truck,
  AlertTriangle,
} from 'lucide-react';
import { PerformanceFormData } from './types';

interface PerformanceGuideTabProps {
  formData: PerformanceFormData;
}

export function PerformanceGuideTab({ formData }: PerformanceGuideTabProps) {
  const excellent = parseFloat(formData.otd_excellent_threshold) || 90;
  const good = parseFloat(formData.otd_good_threshold) || 70;
  const poor = parseFloat(formData.otd_poor_threshold) || 50;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-blue-500" />
            <CardTitle>OTD Performance Guide</CardTitle>
          </div>
          <CardDescription>
            How On-Time Delivery is calculated, how threshold bands work, and where they appear in the portal.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="p-4 border rounded-lg text-center">
              <Truck className="h-8 w-8 mx-auto mb-2 text-blue-500" />
              <div className="font-medium">On-Time Delivery</div>
              <div className="text-sm text-muted-foreground">Measured against Supplier Promise Date</div>
            </div>
            <div className="p-4 border rounded-lg text-center">
              <Clock className="h-8 w-8 mx-auto mb-2 text-green-500" />
              <div className="font-medium">Range Thresholds</div>
              <div className="text-sm text-muted-foreground">Drive card colours and customer quick filters</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-blue-500" />
            <CardTitle>What is OTD?</CardTitle>
          </div>
          <CardDescription>
            On-Time Delivery measures whether supplier deliveries arrived on or before the agreed Supplier Promise Date.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            OTD focuses on <strong>timeliness only</strong> — it does not check whether the full ordered quantity was delivered.
            Each delivery with a Supplier Promise Date is compared to its actual delivery date, as recorded on the supplier&apos;s ASN.
          </p>

          <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg space-y-3">
            <div className="flex items-center gap-2 font-medium">
              <Calculator className="h-4 w-4" />
              OTD Formula
            </div>
            <code className="block text-sm p-3 bg-background rounded border font-mono">
              OTD % = (On-time deliveries) / (Total deliveries with Supplier Promise Date) × 100
            </code>
            <p className="text-sm">
              <strong>On-time:</strong> Delivery date (from ASN) is on or before the Supplier Promise Date.
            </p>
            <p className="text-sm text-muted-foreground">
              Lines without a Supplier Promise Date are excluded from the calculation.
            </p>
          </div>

          <div className="p-4 bg-muted rounded-lg">
            <h4 className="font-medium mb-2">Delivery status values</h4>
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="flex items-center gap-1">
                <Badge className="bg-green-500">On-Time</Badge> Delivery ≤ Promise Date
              </span>
              <span className="flex items-center gap-1">
                <Badge variant="destructive">Late</Badge> Delivery &gt; Promise Date
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Info className="h-5 w-5 text-primary" />
            <CardTitle>Threshold Bands</CardTitle>
          </div>
          <CardDescription>
            Configure these on the OTD Settings tab. They must be ordered: Excellent &gt; Good &gt; Poor.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2 md:grid-cols-3">
            <div className="flex items-center gap-2 p-3 border rounded bg-green-50 dark:bg-green-950/30">
              <span className="w-3 h-3 rounded-full bg-green-500" />
              <span className="text-sm"><strong>Excellent:</strong> ≥ {excellent}%</span>
            </div>
            <div className="flex items-center gap-2 p-3 border rounded bg-yellow-50 dark:bg-yellow-950/30">
              <span className="w-3 h-3 rounded-full bg-yellow-500" />
              <span className="text-sm"><strong>Good:</strong> {good}–{(excellent - 0.01).toFixed(0)}%</span>
            </div>
            <div className="flex items-center gap-2 p-3 border rounded bg-red-50 dark:bg-red-950/30">
              <span className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-sm"><strong>Poor:</strong> &lt; {good}%</span>
            </div>
          </div>

          {poor < good && (
            <p className="text-sm text-muted-foreground">
              The <strong>Poor threshold</strong> ({poor}%) defines the lower bound of a &quot;Fair&quot; band ({poor}–{(good - 0.01).toFixed(0)}%)
              used by quick-filter chips on the Customers page.
            </p>
          )}

          <div className="space-y-2">
            <h4 className="font-medium text-sm">Where thresholds are used</h4>
            <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
              <li><strong>Customers page</strong> — OTD % colour on customer cards</li>
              <li><strong>Customers page</strong> — OTD range quick-filter chips below the search bar</li>
              <li><strong>Customer detail</strong> — OTD performance display (when enabled)</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <CardTitle>Common Questions</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="p-3 border rounded-lg text-sm">
              <div className="font-medium">Which date is used for OTD?</div>
              <p className="text-muted-foreground mt-1">
                The <strong>Supplier Promise Date</strong> on each sales order line. This is the date the supplier committed to deliver by.
              </p>
            </div>
            <div className="p-3 border rounded-lg text-sm">
              <div className="font-medium">Why is a customer showing 0% OTD?</div>
              <p className="text-muted-foreground mt-1">
                Either no deliveries or ASNs have been recorded yet, or none of the delivered lines had a Supplier Promise Date set.
              </p>
            </div>
            <div className="p-3 border rounded-lg text-sm">
              <div className="font-medium">Do threshold changes apply immediately?</div>
              <p className="text-muted-foreground mt-1">
                Yes — after saving, card colours and quick filters use the new bands on the next page load.
                The underlying OTD percentages are unchanged; only the colour and filter boundaries update.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Glossary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 md:grid-cols-2 text-sm">
            <div className="p-2 border-b"><strong>OTD</strong> — On-Time Delivery</div>
            <div className="p-2 border-b"><strong>ASN</strong> — Advanced Shipping Notice (supplier shipment notification)</div>
            <div className="p-2 border-b"><strong>Supplier Promise Date</strong> — Date the supplier committed to deliver by</div>
            <div className="p-2 border-b"><strong>Delivery Date</strong> — Date the supplier&apos;s delivery was completed (from ASN)</div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
